import { ActionIcon, Tooltip } from '@cowbell/ui/core';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBrowser,
  faFile,
  faICursor,
  faPlus,
  faTag,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Context } from '../../integrations/context-types';
import { useShellContexts } from '../../provider';
import { derivedFingerprint } from '../../state/context-fingerprints';
import { useMooConfig } from '../../provider/internal-context';
import {
  CloseIcon,
  ThumbtackFilledIcon,
  ThumbtackIcon,
} from '../ShellDrawer/icons';
import styles from './ContextChipList.module.css';
import token from './Token.module.css';

// Common keys an entity's display name might live under in `entityData`.
// V1's per-type config picks more precise fields (e.g. submissions use
// `companyName` / `insuredName`); these fallbacks cover the same ground
// without pulling the host's entity-config map into the library.
const NAME_KEYS = [
  'name',
  'companyName',
  'insuredName',
  'displayName',
  'title',
  'subject',
];

// Common keys for the *friendly* entity identifier (SUB-VYPO1BI, CLM-…,
// POL-…). The route gives us only a UUID; the host's entity-resolver
// fetches the friendly number into `entityData.number`.
const NUMBER_KEYS = ['number', 'code', 'friendlyId'];

function pickStringFrom(
  data: Record<string, unknown> | undefined,
  keys: readonly string[]
): string {
  if (!data) return '';
  for (const key of keys) {
    const v = data[key];
    if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  }
  return '';
}

function entityNameFrom(data: Record<string, unknown> | undefined): string {
  return pickStringFrom(data, NAME_KEYS);
}

function entityDisplayId(
  data: Record<string, unknown> | undefined,
  fallback: string
): string {
  return pickStringFrom(data, NUMBER_KEYS) || fallback;
}

// Kinds whose capitalized form isn't just "first letter up" — e.g. an
// acronym prefix ("UW" in "uw-review", not "Uw-review").
const PREFIX_OVERRIDES: Record<string, string> = {
  'uw-review': 'UW-review',
};

function capitalizeKind(kind: string): string {
  const override = PREFIX_OVERRIDES[kind.toLowerCase()];
  if (override) return override;
  return kind.length === 0
    ? kind
    : kind.charAt(0).toUpperCase() + kind.slice(1);
}

/** Row label split into a bold type prefix ("Quote:") and the value text
 *  next to it — mirrors V1's `EntityContextRow` / `DocumentContextRow` /
 *  `SelectionContextRow` layout. */
function rowPrefixFor(c: Context): string {
  if (c.kind === 'document') return (c.fileType || 'file').toUpperCase();
  if (c.kind === 'selection') return 'Selection';
  if (c.kind === 'entity') return capitalizeKind(c.entityType);
  return c.entityType ? capitalizeKind(c.entityType) : 'Page';
}

interface RowValueParts {
  /** The name a user actually relates to — rendered first, at full weight. */
  readonly name?: string;
  /** The raw identifier — rendered after the name, de-emphasized. Also the
   *  whole value when no name is available. */
  readonly id: string;
}

function rowValuePartsFor(c: Context): RowValueParts {
  if (c.kind === 'document') return { id: c.fileName };
  if (c.kind === 'selection') return { id: c.content };
  if (c.kind === 'entity') {
    const friendlyId = pickStringFrom(c.entityData, NUMBER_KEYS);
    const name = entityNameFrom(c.entityData);
    if (name && friendlyId) return { name, id: friendlyId };
    if (name) return { name, id: '' };
    return { id: entityDisplayId(c.entityData, c.entityId) };
  }
  if (c.entityType && c.entityId) {
    const friendlyId = pickStringFrom(c.entityData, NUMBER_KEYS);
    const name = entityNameFrom(c.entityData);
    if (name && friendlyId) return { name, id: friendlyId };
    if (name) return { name, id: '' };
    return { id: entityDisplayId(c.entityData, c.entityId) };
  }
  return { id: c.view };
}

function kindIcon(
  c: Context,
  entityIconResolver:
    | ((entityType: string) => IconDefinition | undefined)
    | undefined
): IconDefinition {
  if (c.kind === 'document') return faFile;
  if (c.kind === 'selection') return faICursor;
  if (c.kind === 'screen' && c.entityType) {
    return entityIconResolver?.(c.entityType) ?? faTag;
  }
  if (c.kind === 'screen') return faBrowser;
  if (c.kind === 'entity' && c.entityType) {
    return entityIconResolver?.(c.entityType) ?? faTag;
  }
  return faTag;
}

/** Screen contexts without `entityType` are list-view page scope: they
 *  reach the agent silently and never render as a token. */
function isShownAsToken(c: Context): boolean {
  return c.kind !== 'screen' || !!c.entityType;
}

function isPinnable(c: Context): boolean {
  return c.kind === 'entity' || c.kind === 'screen';
}

/** Hover-card placement shared by every token, live or offered back. */
const TOKEN_TIP_PROPS = {
  openDelay: 400,
  multiline: true,
  w: '18rem',
  position: 'top-start',
  classNames: { tooltip: token.tip },
} as const;

interface TokenProps {
  readonly context: Context;
  readonly isDerived?: boolean;
  readonly onRemove: () => void;
  readonly onPinToggle: () => void;
}

/** One attached context as a compact token: type icon, the name a person
 *  recognises, and its identifier muted after it. The type reads from the
 *  icon and the tooltip; pin and remove appear on hover or focus. */
function ContextToken({
  context,
  isDerived = false,
  onRemove,
  onPinToggle,
}: TokenProps) {
  const { entityIconResolver } = useMooConfig();
  const pinned = context.pinned === true;
  const pinnable = isPinnable(context);
  const prefix = rowPrefixFor(context);
  const { name, id } = rowValuePartsFor(context);

  return (
    <Tooltip
      label={
        <TokenTip
          type={prefix}
          origin={isDerived ? 'From this page' : undefined}
          name={name || id}
          id={name ? id : ''}
        />
      }
      {...TOKEN_TIP_PROPS}
    >
      <div
        className={token.token}
        data-derived={isDerived || undefined}
        data-pinned={pinned || undefined}
      >
        <FontAwesomeIcon
          icon={kindIcon(context, entityIconResolver)}
          className={token.tokenIcon}
        />
        <span className={token.srOnly}>{prefix}:</span>
        <span className={token.tokenName}>{name || id}</span>
        {name && id && <span className={token.tokenMeta}>{id}</span>}
        <span className={token.tokenActions}>
          {pinnable && (
            <ActionIcon
              className={token.tokenAction}
              variant="transparent"
              onClick={onPinToggle}
              aria-label={pinned ? 'Unpin context' : 'Pin context'}
              aria-pressed={pinned}
            >
              {pinned ? (
                <ThumbtackFilledIcon size={12} />
              ) : (
                <ThumbtackIcon size={12} />
              )}
            </ActionIcon>
          )}
          <ActionIcon
            className={token.tokenAction}
            variant="transparent"
            onClick={onRemove}
            aria-label="Remove context"
          >
            <CloseIcon size={12} />
          </ActionIcon>
        </span>
      </div>
    </Tooltip>
  );
}

/** A derived context the user dismissed on this page, offered back as a
 *  quiet dashed token. Clicking it attaches the context again. */
function ReattachToken({
  context,
  onReattach,
}: Readonly<{ context: Context; onReattach: () => void }>) {
  const prefix = rowPrefixFor(context);
  const { name, id } = rowValuePartsFor(context);
  const label = name || id;

  return (
    <Tooltip
      label={
        <TokenTip
          type={prefix}
          origin="Not attached"
          name={label}
          id={name ? id : ''}
        />
      }
      {...TOKEN_TIP_PROPS}
    >
      <button
        type="button"
        className={token.token}
        data-ghost="true"
        onClick={onReattach}
        aria-label={`Attach ${label} again`}
      >
        <FontAwesomeIcon icon={faPlus} className={token.tokenIcon} />
        <span className={token.tokenName}>{label}</span>
      </button>
    </Tooltip>
  );
}

/** The token's hover card: what kind of thing it is (and whether it came
 *  from the page), the name, and the identifier on its own line. */
function TokenTip({
  type,
  origin,
  name,
  id,
}: Readonly<{ type: string; origin?: string; name: string; id: string }>) {
  return (
    <span className={token.tipBody}>
      <span className={token.tipType}>
        {origin ? `${type} · ${origin}` : type}
      </span>
      <span className={token.tipName}>{name}</span>
      {id && <span className={token.tipId}>{id}</span>}
    </span>
  );
}

/** Renders the attached contexts as tokens in the composer's token row
 *  (the wrapper is display: contents, so they flow alongside upload
 *  tokens). Derived contexts — auto-derived from the current route — come
 *  first, user-attached ones after. Screen contexts without `entityType` are
 *  list-view page-scope contexts: they carry route info to the agent silently
 *  and are not shown (V1 parity). */
export function ContextChipList() {
  const {
    user,
    derived,
    pin,
    unpin,
    remove,
    pinDerived,
    unpinDerived,
    removeDerived,
    dismissedDerived,
    restoreDerived,
  } = useShellContexts();

  // Preserve original indices so pin/remove dispatch the correct index into
  // the full derived array, then filter to only visible (entity-detail) rows.
  const visibleDerived = derived
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => isShownAsToken(c));
  const reattachable = dismissedDerived.filter(isShownAsToken);

  if (
    user.length === 0 &&
    visibleDerived.length === 0 &&
    reattachable.length === 0
  ) {
    return null;
  }

  return (
    <div className={styles.contextBlock} data-testid="context-tokens">
      {visibleDerived.map(({ c, i }) => (
        <ContextToken
          key={`derived:${c.kind}:${i}`}
          context={c}
          isDerived
          onRemove={() => removeDerived(i)}
          onPinToggle={() =>
            c.pinned === true ? unpinDerived(i) : pinDerived(i)
          }
        />
      ))}
      {user.map((c, i) => (
        <ContextToken
          key={`user:${c.kind}:${i}`}
          context={c}
          onRemove={() => remove(i)}
          onPinToggle={() => (c.pinned === true ? unpin(i) : pin(i))}
        />
      ))}
      {reattachable.map((c) => (
        <ReattachToken
          key={`reattach:${derivedFingerprint(c)}`}
          context={c}
          onReattach={() => restoreDerived(c)}
        />
      ))}
    </div>
  );
}
